import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

/**
 * Top-bar banner shown to users whose `emailVerified === false` (typically
 * those created via the OTP-skip fallback when the Resend domain wasn't
 * verified at signup). One click → modal → "Send code" → enter the code →
 * `emailVerified` flips true and the banner disappears.
 *
 * Dismissable per-session (sessionStorage) so users don't get nagged once
 * they've acknowledged it, but it returns on the next sign-in.
 */
export default function EmailVerificationBanner() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | sent | confirming
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("dismissEmailVerifyBanner") === "1"; }
    catch { return false; }
  });

  if (!user || user.emailVerified !== false || dismissed) return null;

  const sendCode = async () => {
    setSending(true);
    try {
      await api.post("/auth/verify-email/send");
      setStage("sent");
      toast.success(`Verification code sent to ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the code right now.");
    } finally { setSending(false); }
  };

  const confirmCode = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setConfirming(true);
    try {
      await api.post("/auth/verify-email/confirm", { code: c });
      toast.success("Email verified ✓");
      await refresh();
      setOpen(false);
      setStage("idle");
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code.");
    } finally { setConfirming(false); }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem("dismissEmailVerifyBanner", "1"); } catch (_e) {}
    setDismissed(true);
  };

  return (
    <>
      <div
        className="w-full bg-secondary/15 border-b border-secondary/30 px-4 py-2.5 flex items-center gap-3 text-foreground/85"
        data-testid="email-verify-banner"
      >
        <Mail className="w-4 h-4 text-secondary flex-shrink-0" />
        <div className="text-sm leading-tight flex-1 min-w-0">
          <span className="font-medium">Verify your email</span>
          <span className="text-foreground/65 ml-2 hidden sm:inline">
            We couldn't send the verification code at signup — claim your account now.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs uppercase tracking-wider bg-secondary text-secondary-foreground hover:bg-secondary/85 px-3 py-1.5 rounded-sm transition flex-shrink-0"
          data-testid="email-verify-open-btn"
        >
          Verify
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-foreground/45 hover:text-foreground/80 transition flex-shrink-0 p-1"
          data-testid="email-verify-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="parchment max-w-md"
          data-testid="email-verify-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Verify your email</DialogTitle>
            <DialogDescription className="text-foreground/70 leading-relaxed">
              We'll send a 6-digit code to <strong className="text-secondary">{user.email}</strong>.
              Enter it below to claim your account.
            </DialogDescription>
          </DialogHeader>

          {stage === "idle" ? (
            <div className="space-y-4 pt-2">
              <button
                type="button"
                onClick={sendCode}
                disabled={sending}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/85 py-3 rounded-sm uppercase tracking-wider text-sm transition disabled:opacity-50"
                data-testid="email-verify-send-btn"
              >
                {sending ? "Sending…" : "Send verification code"}
              </button>
              <p className="text-xs text-foreground/60 leading-relaxed">
                Already have a code? <button onClick={() => setStage("sent")} className="text-secondary hover:underline" data-testid="email-verify-have-code-btn">Enter it now</button>
              </p>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="w-full text-center text-3xl font-mono tracking-[0.4em] bg-input border border-secondary/30 focus:border-secondary p-4 rounded-sm"
                autoFocus
                data-testid="email-verify-code-input"
              />
              <button
                type="button"
                onClick={confirmCode}
                disabled={confirming || code.length !== 6}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/85 py-3 rounded-sm uppercase tracking-wider text-sm transition disabled:opacity-50"
                data-testid="email-verify-confirm-btn"
              >
                {confirming ? "Verifying…" : "Verify"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={sending}
                  className="text-secondary hover:underline"
                  data-testid="email-verify-resend-btn"
                >
                  {sending ? "Sending…" : "Resend code"}
                </button>
                <span className="text-foreground/55">Code expires in 10 min</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
